import React from "react";
import { motion } from "framer-motion";
import { FaAward, FaCalendarAlt, FaExternalLinkAlt } from "react-icons/fa";

// Import logos
import bcsLogo from "../../assets/images/bcs.png";
import ciisecLogo from "../../assets/images/ciisec.jpeg";
import ieeeLogo from "../../assets/images/ieee.png";

const fellowships = [
  {
    id: 1,
    title: "Fellow of BCS",
    organization: "British Computer Society",
    description: "Recognized for contributions and leadership in cybersecurity",
    year: "2023",
    logo: bcsLogo,
    link: "#",
    highlight: true,
  },
  {
    id: 2,
    title: "Fellow of CIISec",
    organization: "Chartered Institute of Information Security",
    description:
      "Awarded fellowship for expertise and influence in the field of cybersecurity",
    year: "2023",
    logo: ciisecLogo,
    link: "#",
    highlight: true,
  },
  {
    id: 3,
    title: "Senior Member of IEEE",
    organization: "Institute of Electrical and Electronics Engineers",
    description:
      "Awarded Senior Membership for significant performance and contributions to cloud security",
    year: "2022",
    logo: ieeeLogo,
    link: "#",
  },
];

const FellowshipsSection = () => {
  return (
    <section className="mb-16">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-8 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
          Awards & Fellowships
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {fellowships.map((fellowship, index) => (
            <motion.div
              key={fellowship.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.05 }}
              className={`relative overflow-hidden rounded-xl ${
                fellowship.highlight
                  ? "bg-gradient-to-br from-blue-500/20 to-purple-500/20 border-2 border-blue-400/50"
                  : "bg-white/5 border border-white/10"
              } backdrop-blur-sm p-6 hover:bg-white/10 transition-all duration-300 group`}
            >
              {fellowship.highlight && (
                <div className="absolute top-0 right-0 bg-gradient-to-r from-blue-500 to-purple-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                  FEATURED
                </div>
              )}

              <div className="flex flex-col items-center text-center">
                {/* Logo */}
                <div className="w-20 h-20 bg-white/10 rounded-full p-3 flex items-center justify-center mb-4 group-hover:bg-white/20 transition-colors">
                  {fellowship.logo ? (
                    <img
                      src={fellowship.logo}
                      alt={fellowship.organization}
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <FaAward className="text-3xl text-blue-400" />
                  )}
                </div>

                {/* Content */}
                <h3 className="text-xl font-semibold text-white mb-2 group-hover:text-blue-400 transition-colors">
                  {fellowship.title}
                </h3>

                <p className="text-sm text-gray-400 mb-3">
                  {fellowship.organization}
                </p>

                <p className="text-sm text-gray-300 mb-4">
                  {fellowship.description}
                </p>

                {/* Footer */}
                <div className="flex items-center justify-between w-full mt-auto pt-4 border-t border-white/10">
                  <div className="flex items-center text-sm text-gray-400">
                    <FaCalendarAlt className="mr-2" />
                    {fellowship.year}
                  </div>
                  <a
                    href={fellowship.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    <FaExternalLinkAlt />
                  </a>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Additional Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          className="mt-8 bg-gradient-to-r from-blue-500/10 to-purple-500/10 backdrop-blur-sm border border-white/10 rounded-xl p-6 text-center"
        >
          <p className="text-gray-300">
            These fellowships and memberships represent recognition from leading
            institutions for contributions to cybersecurity research,
            innovation, and leadership in the field.
          </p>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default FellowshipsSection;
